/**
 * เจตนา & กฎวิเคราะห์ — "สมอง" ที่ระบบใช้ดูดคำถาม-คำตอบจากแชท + จับเจตนาลูกค้า (NO HARDCODE)
 * - kb_intents: เจตนาที่ใช้เสนอ FAQ (keyword จับคำถาม + answer_keyword คัดคำตอบให้ตรง)
 * - analysis_rules: กฎ junk / multicolor / คำจับ สี-สต๊อก-สัดส่วน-ออเดอร์ (บอตใช้จับเจตนาลูกค้า)
 * แก้ที่นี่ → ระบบวิเคราะห์/บอท ใช้ค่าใหม่ทันที (cache 60s)
 *
 * ใช้ CrudManager กลาง 2 ชุด
 */
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { KbIntentsApi, AnalysisRulesApi, type KbIntentItem, type AnalysisRuleItem } from '@/lib/admin-api';

const INTENT_FIELDS: CrudField[] = [
  { key: 'intent_key', label: 'รหัสเจตนา (key)', type: 'text', required: true, placeholder: 'ask_price', disabledOnEdit: true, help: 'ตัวระบุภายใน (อังกฤษ) ห้ามซ้ำ' },
  { key: 'question', label: 'คำถามตัวแทน', type: 'text', required: true, placeholder: 'ราคาเท่าไหร่', help: 'คำถามหลักที่เจตนานี้แทน' },
  { key: 'category', label: 'หมวด', type: 'text', required: true, default: 'ทั่วไป', help: 'จัดกลุ่มเจตนา' },
  { key: 'keywords', label: 'keyword จับคำถาม', type: 'chips', placeholder: 'ราคา, กี่บาท', help: 'ถ้าลูกค้าพิมพ์คำเหล่านี้ = เข้าเจตนานี้' },
  { key: 'answer_keywords', label: 'keyword คัดคำตอบ', type: 'chips', placeholder: 'บาท, โปร', help: 'ใช้คัดคำตอบจากแชทให้ตรงเจตนา' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
];

const RULE_TYPES = [
  { value: 'junk', label: 'junk (คำตอบมั่ว)' },
  { value: 'multicolor', label: 'multicolor (รูปรวมหลายสี)' },
  { value: 'color_kw', label: 'color_kw (คำจับ “ถามสี”)' },
  { value: 'stock_kw', label: 'stock_kw (คำจับ “ถามสต๊อก”)' },
  { value: 'measure_kw', label: 'measure_kw (คำจับสัดส่วน อก/เอว/สะโพก)' },
  { value: 'order_verb', label: 'order_verb (คำสั่งซื้อ)' },
  { value: 'order_unit_bundle', label: 'order_unit_bundle (หน่วย “ชุด”)' },
  { value: 'order_unit_piece', label: 'order_unit_piece (หน่วยแยกชิ้น)' },
  { value: 'order_start', label: 'order_start (คำเริ่มรับออเดอร์)' },
  { value: 'order_done', label: 'order_done (คำสรุปจบออเดอร์)' },
  { value: 'order_confirm', label: 'order_confirm (คำยืนยันออเดอร์)' },
  { value: 'order_correct', label: 'order_correct (คำแก้ไขออเดอร์)' },
];
const ruleLabel = (t: string) => RULE_TYPES.find((x) => x.value === t)?.label ?? t;

const RULE_FIELDS: CrudField[] = [
  { key: 'rule_type', label: 'ประเภทกฎ', type: 'select', required: true, options: RULE_TYPES, default: 'color_kw', help: 'บอตเอากฎนี้ไปใช้ตอนไหน' },
  { key: 'pattern', label: 'คำ/แพทเทิร์น', type: 'text', required: true, placeholder: 'สีอะไร', help: 'คำเดียวต่อ 1 แถว' },
  { key: 'note', label: 'หมายเหตุ', type: 'text', help: 'บันทึกภายใน' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
];

export default function IntentsPage() {
  return (
    <div className="space-y-8">
      <CrudManager<KbIntentItem>
        title="🎯 เจตนา (จับคำถามจากแชท)"
        addLabel="เพิ่มเจตนา"
        help="ใช้ดูดคำถาม-คำตอบจากแชทจริงมาเสนอเป็น FAQ — keyword จับคำถาม + answer_keyword คัดคำตอบให้ตรง"
        queryKey="kb-intents"
        api={KbIntentsApi}
        fields={INTENT_FIELDS}
        brandScoped
        hardDelete
        searchKeys={['question', 'keywords', 'category']}
        groupBy={(r) => r.category || 'ทั่วไป'}
        rowTitle={(r) => <>{r.question}</>}
        rowSubtitle={(r) => <>🔑 {r.keywords || '—'} · คัดคำตอบ: {r.answer_keywords || '—'}</>}
      />

      <CrudManager<AnalysisRuleItem>
        title="⚠️ กฎวิเคราะห์ (บอตใช้จับเจตนาลูกค้า)"
        addLabel="เพิ่มกฎ"
        help="junk = คำที่ทำให้คำตอบมั่ว · color_kw/stock_kw/measure_kw = คำที่บอตใช้จับว่าลูกค้าถามเรื่องอะไร · order_* = คำรับออเดอร์"
        queryKey="analysis-rules"
        api={AnalysisRulesApi}
        fields={RULE_FIELDS}
        brandScoped
        hardDelete
        searchKeys={['pattern', 'rule_type', 'note']}
        groupBy={(r) => ruleLabel(r.rule_type)}
        sortRows={(a, b) => a.rule_type.localeCompare(b.rule_type) || a.id - b.id}
        rowTitle={(r) => <>{r.pattern}</>}
        rowSubtitle={(r) => (r.note ? <>{r.note}</> : null)}
      />
    </div>
  );
}
